import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { CALLS } from 'constants/scopes.js';
import usePermissions, { getPermissionFunction } from 'hooks/usePermissions.jsx';
import useClientPermissions from 'hooks/useClientPermissions.js';
import usePlayerVersion from 'hooks/usePlayerVersion.js';
import useCallSettings from 'hooks/useCallSettings.js';
import { READ } from 'shared-permissions';
import { buildRoute, DASHBOARD_TRANSLATIONS, GLOBAL, SAML_PROVIDERS, CLIENTS, ORG_INTEGRATIONS } from 'constants/routes.js';

import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import {
  ONE_TO_MANY_PRODUCT,
  ON_DEMAND_PRODUCT,
  ONE_TO_ONE_PRODUCT,
  SUPERADMIN_PRODUCT,
  CLIENT_PRODUCT,
  CHAT_PRODUCT,
  ORG_PRODUCTS,
} from 'constants/products.js';
import { useOrgSettings } from 'components/OrgSelector/useOrgSettings.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import useGroupManager from '../useGroupManager.js';
import { useOrgUser } from 'contexts/OrgUserContext.jsx';
import ONE_TO_MANY_ROUTES from './oneToManyRoutes.js';
import ONE_TO_ONE_ROUTES from './oneToOneRoutes.js';
import ON_DEMAND_ROUTES from './onDemandRoutes.js';
import CLIENT_ROUTES from './clientRoutes.js';
import CHAT_ROUTES from './chatRoutes.js';
import useMetaAccessTokens from 'hooks/useMetaAccessTokens.js';
import useCallouts from 'hooks/useCallouts.jsx';

export const SUPERADMIN_ROUTES = [
  {
    title: 'adminTitle',
    route: GLOBAL,
    icon: 'list',
  },
  {
    title: 'Clients',
    route: CLIENTS,
    icon: 'list',
  },
  {
    title: 'Dashboard Translations',
    route: DASHBOARD_TRANSLATIONS,
    icon: 'settings',
  },
  {
    title: 'SAML providers',
    route: SAML_PROVIDERS,
    icon: 'list',
  },
];

const shouldShowSuperadminSidebar = (isSuperadmin, masqueradeMode, selectedProduct) =>
  isSuperadmin && !masqueradeMode && selectedProduct === SUPERADMIN_PRODUCT;

const getRoutesBySelectedProduct = (selectedProduct) => {
  if (selectedProduct === ONE_TO_MANY_PRODUCT) return ONE_TO_MANY_ROUTES;
  if (selectedProduct === ONE_TO_ONE_PRODUCT) return ONE_TO_ONE_ROUTES;
  if (selectedProduct === ON_DEMAND_PRODUCT) return ON_DEMAND_ROUTES;
  if (selectedProduct === CLIENT_PRODUCT) return CLIENT_ROUTES;
  if (selectedProduct === CHAT_PRODUCT) return CHAT_ROUTES;

  return [];
};

export const getNavigationElements = ({
  authUser,
  masqueradeMode,
  orgId,
  clientId,
  features,
  selectedProduct,
  orgSettings = {},
  callsSettings = {},
  permissions,
  isGroupManager,
  userData,
  dashboardPlayerVersion,
}) => {
  if (!authUser) return [];
  if (shouldShowSuperadminSidebar(authUser.isSuperadmin, masqueradeMode, selectedProduct)) return SUPERADMIN_ROUTES;
  if (!clientId) {
    if (!orgId && ORG_PRODUCTS.includes(selectedProduct)) return [];
    if (!selectedProduct) return [];
  }

  // When we activate the new showsStatsEmbeddableInsights, we want to remove legacy stats from sidebar
  // Once we completely remove legacy stats, we can remove this as well
  features.legacyStatsEnabled = !features?.showStatsEmbeddableInsightsEnabled;

  const { calls: { callsBaseUrl, agentToolUrl } = {} } = orgSettings;

  const masqueradeModeFilter = ({ shownInMasquerade }) => {
    if (typeof shownInMasquerade === 'boolean' && !shownInMasquerade) return !masqueradeMode;
    return true;
  };

  /**
   * FEATURE FLAG PROCESSING:
   *
   * Supports positive and negative flag patterns for route visibility:
   * This is useful for using one flag to hide and show multiple routes
   *
   * POSITIVE: ['vodEnabled'] - shows when feature = true
   * NEGATIVE: ['!distributionV2Enabled'] - shows when feature = false
   * MIXED: ['featureA', '!featureB'] - shows when featureA=true AND featureB=false
   *
   * EXAMPLE:
   * ['productsNavigationEnabled', 'vodEnabled', '!distributionV2Enabled']
   * → shows when: productsNavigationEnabled=true AND vodEnabled=true AND distributionV2Enabled=false
   */
  const featuresFilter = ({ requiredFeatures }) => {
    if (!requiredFeatures) return true;
    return requiredFeatures.every((feature) => {
      if (feature.startsWith('!')) {
        // Negative feature requirement - feature should be falsy
        const featureName = feature.slice(1);
        return !features[featureName];
      } else {
        // Positive feature requirement - feature should be truthy
        return features[feature];
      }
    });
  };

  const permissionFilter = ({ requiredPermission, allowSuperAdmin, requireGroupManagerRole }) => {
    if (requireGroupManagerRole) return isGroupManager;
    if (!requiredPermission) return true;

    const hasRequiredPermission = (() => {
      if (typeof requiredPermission === 'string') return getPermissionFunction(permissions)(requiredPermission, READ);
      return requiredPermission?.some((permission) => getPermissionFunction(permissions)(permission, READ));
    })();

    return hasRequiredPermission || authUser.isSuperadmin && !!allowSuperAdmin;
  };

  const emptySubmenuFilter = ({ submenu }) => {
    if (!submenu) return true;
    return submenu.length > 0;
  };

  const customFilter = ({ customFilterFunction }) => {
    if (typeof customFilterFunction !== 'function') return true;
    return customFilterFunction({
      authUser,
      orgSettings,
      callsSettings,
      permissions,
      features,
      userData,
      dashboardPlayerVersion,
    });
  };

  const agentToolReplace = (menuItem) => {
    if (menuItem.title !== 'agentToolTitle') return menuItem;
    if (agentToolUrl) return menuItem;
    if (!callsBaseUrl) return menuItem;

    return {
      ...menuItem,
      url: `${callsBaseUrl}/agent`,
    };
  };

  const ROUTES = getRoutesBySelectedProduct(selectedProduct);

  // global product flag calls / shows
  return ROUTES.map((route) => {
    if (!route.submenu) return route;

    const filteredSubmenu = route.submenu.filter(featuresFilter).filter(permissionFilter).filter(masqueradeModeFilter).filter(customFilter);
    return { ...route, submenu: filteredSubmenu };
  })
    .filter(emptySubmenuFilter)
    .filter(featuresFilter)
    .filter(masqueradeModeFilter)
    .filter(permissionFilter)
    .filter(customFilter)
    .map(agentToolReplace);
};

const useNavigation = () => {
  const { dashboardPlayerVersion } = usePlayerVersion();
  const orgSettings = useOrgSettings([CALLS]);
  const { settings: callsSettings } = useCallSettings();
  const { selectedProduct, orgId, clientId } = useProductDataOutsideRoutes();
  const location = useLocation();
  const { t } = useLocalization();
  const { permissions } = usePermissions();
  const { clientPermissions } = useClientPermissions({ clientId });
  const authUser = useSelector((state) => state.sessionState.authUser);
  const masqueradeMode = useSelector((state) => state.ui.masqueradeMode);
  const [openedSubMenu, setOpenedSubMenu] = useState(null);
  const { features } = useFeatureContext();
  const { isGroupManager } = useGroupManager({ orgId, selectedProduct });
  const { userData } = useOrgUser();
  const { needsRefresh } = useMetaAccessTokens();
  const { callouts } = useCallouts();

  const { metaConnectionRefreshNotificationEnabled } = features;

  const navigationElements = getNavigationElements({
    authUser,
    masqueradeMode: !!masqueradeMode,
    orgId,
    clientId,
    features,
    selectedProduct,
    orgSettings,
    callsSettings,
    permissions: { ...permissions, ...clientPermissions },
    isGroupManager,
    userData,
    dashboardPlayerVersion,
  });
  const translatedNavigationElements = navigationElements.map((navigationElement) => {
    const updatedSubmenu = {
      submenu: navigationElement.submenu?.map((submenuItem) => ({
        ...submenuItem,
        title: t(submenuItem.title) || submenuItem.title,
      })),
    };
    return {
      ...navigationElement,
      title: t(navigationElement.title) || navigationElement.title,
      ...(navigationElement.submenu ? updatedSubmenu : {}),
    };
  })
    .map((navigationElement) => {
      const idForCallout = `sidebarFlair-${navigationElement.icon}`;
      const callout = callouts?.find((callout) => callout.id === idForCallout || callout.ids?.includes(idForCallout));
      if (callout) {
        return {
          ...navigationElement,
          flair: { id: idForCallout },
        };
      }
      if (![ONE_TO_MANY_PRODUCT, ON_DEMAND_PRODUCT].includes(selectedProduct)) return navigationElement;
      if (!metaConnectionRefreshNotificationEnabled) return navigationElement;

      // Add a flair icon to the settings icon if the user needs to refresh the access tokens
      if (needsRefresh && navigationElement.icon === 'settings') {
        const { submenu } = navigationElement;
        return {
          ...navigationElement,
          flair: { name: 'info' },
          // Also add a flair icon to the integrations submenu item
          submenu: submenu.map((submenuItem) => {
            if (submenuItem.route === ORG_INTEGRATIONS) {
              return {
                ...submenuItem,
                flair: { name: 'info' },
              };
            }
            return submenuItem;
          }),
        };
      }
      return navigationElement;
    });

  useEffect(() => {
    const title = translatedNavigationElements
      .filter((element) => element.submenu)
      .filter((element) =>
        element.submenu.find(({ route }) =>
          location.pathname.includes(buildRoute(route, { orgId, selectedProduct, clientId })),
        ),
      )
      .map((element) => element.title)[0];

    setOpenedSubMenu(title);
  }, [orgId, location.pathname, translatedNavigationElements, selectedProduct]);

  return { navigationElements: translatedNavigationElements, openedSubMenu, setOpenedSubMenu };
};

export default useNavigation;
